import axios from "axios";
import { endpoints } from "../api/endpoint";
import { useEffect, useRef, useState, useCallback } from "react";

const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  ALLOWED: "allowed",
  DENIED: "denied",
  ERROR: "error",
};

const INITIAL_STATE = {
  status: STATUS.IDLE,
  decision: null,
  message: null,
  denialReason: null,
  error: null,
};

export default function useSwapAccess({ stationId, driverId }) {
  const [state, setState] = useState(INITIAL_STATE);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!stationId || !driverId) {
      setState(INITIAL_STATE);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      status: STATUS.LOADING,
      decision: null,
      message: null,
      denialReason: null,
      error: null,
    });

    axios
      .get(endpoints.evaluateSwapAccess, {
        params: { stationId, driverId },
        timeout: 5000,
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (data.decision === "ALLOW") {
          setState({
            status: STATUS.ALLOWED,
            decision: "ALLOW",
            message: null,
            denialReason: null,
            error: null,
          });
        } else {
          setState({
            status: STATUS.DENIED,
            decision: data.decision || "DENY",
            message: data.message || "Access denied",
            denialReason: data.denialReason || null,
            error: null,
          });
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;

        const message =
          err.response?.data?.message ||
          err.message ||
          "Swap access check failed";

        setState({
          status: STATUS.ERROR,
          decision: null,
          message,
          denialReason: null,
          error: message,
        });
      });

    return () => {
      controller.abort();
    };
  }, [stationId, driverId]);

  const retry = useCallback(() => {
    if (!stationId || !driverId) return;

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      status: STATUS.LOADING,
      decision: null,
      message: null,
      denialReason: null,
      error: null,
    });

    axios
      .get(endpoints.evaluateSwapAccess, {
        params: { stationId, driverId },
        timeout: 5000,
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (data.decision === "ALLOW") {
          setState({
            status: STATUS.ALLOWED,
            decision: "ALLOW",
            message: null,
            denialReason: null,
            error: null,
          });
        } else {
          setState({
            status: STATUS.DENIED,
            decision: data.decision || "DENY",
            message: data.message || "Access denied",
            denialReason: data.denialReason || null,
            error: null,
          });
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;

        const message =
          err.response?.data?.message ||
          err.message ||
          "Swap access check failed";

        setState({
          status: STATUS.ERROR,
          decision: null,
          message,
          denialReason: null,
          error: message,
        });
      });
  }, [stationId, driverId]);

  return {
    ...state,
    isLoading: state.status === STATUS.LOADING,
    isAllowed: state.status === STATUS.ALLOWED,
    isDenied: state.status === STATUS.DENIED,
    isError: state.status === STATUS.ERROR,
    retry,
  };
}
